/* ================================================
   MAUVAIS GARÇON — Window Manager
   Draggable, stackable, minimizable OS windows.
   ================================================ */

class MGWindowManager {
  constructor() {
    this._z      = 100;
    this._reg    = {};   // id → { el, minimized }
    this._taskBtns = {}; // id → <button>
    this._init();
  }

  _init() {
    document.querySelectorAll('.mg-window').forEach(w => this._register(w));
  }

  _register(winEl) {
    const id = winEl.id;
    if (!id || this._reg[id]) return;

    this._reg[id] = { el: winEl, minimized: false };

    const bar = winEl.querySelector('.mg-window-titlebar');
    if (bar) this._makeDraggable(winEl, bar);

    winEl.addEventListener('mousedown',  () => this._front(winEl));
    winEl.addEventListener('touchstart', () => this._front(winEl), { passive: true });

    winEl.querySelector('.mg-btn-close')
      ?.addEventListener('click', e => { e.stopPropagation(); this.close(id); });

    winEl.querySelector('.mg-btn-minimize')
      ?.addEventListener('click', e => { e.stopPropagation(); this.minimize(id); });

    // Only add to taskbar if visible — hidden ad/game windows register when first shown
    if (winEl.style.display !== 'none') {
      const title = winEl.querySelector('.mg-window-title')?.textContent || id;
      this._addTaskBtn(id, title);
    }
  }

  _makeDraggable(winEl, handle) {
    let on = false, sx, sy, sl, st;

    const start = (cx, cy) => {
      on = true;
      sx = cx; sy = cy;
      sl = winEl.offsetLeft;
      st = winEl.offsetTop;
      this._front(winEl);
    };

    const move = (cx, cy) => {
      if (!on) return;
      const deskW = document.getElementById('mg-desktop')?.offsetWidth  || window.innerWidth;
      const deskH = document.getElementById('mg-desktop')?.offsetHeight || window.innerHeight;
      const newL = Math.max(0, Math.min(sl + cx - sx, deskW  - 40));
      const newT = Math.max(0, Math.min(st + cy - sy, deskH  - 26));
      winEl.style.left = newL + 'px';
      winEl.style.top  = newT + 'px';
    };

    handle.addEventListener('mousedown', e => {
      if (e.target.closest('.mg-window-controls')) return;
      start(e.clientX, e.clientY);
      e.preventDefault();
    });

    handle.addEventListener('touchstart', e => {
      if (e.target.closest('.mg-window-controls')) return;
      const t = e.touches[0];
      start(t.clientX, t.clientY);
    }, { passive: true });

    document.addEventListener('mousemove', e => move(e.clientX, e.clientY));
    document.addEventListener('touchmove', e => {
      const t = e.touches[0];
      move(t.clientX, t.clientY);
    }, { passive: true });

    const stop = () => { on = false; };
    document.addEventListener('mouseup',  stop);
    document.addEventListener('touchend', stop);
  }

  _front(winEl) {
    this._z++;
    winEl.style.zIndex = this._z;
    // Update taskbar button state
    const id = winEl.id;
    Object.keys(this._taskBtns).forEach(k => {
      this._taskBtns[k]?.classList.toggle('mg-btn-active', k === id);
    });
  }

  _addTaskBtn(id, title) {
    const bar = document.getElementById('mg-taskbar-windows');
    if (!bar) return;
    const btn = document.createElement('button');
    btn.className = 'mg-taskbar-btn';
    btn.id = 'tbtn-' + id;
    btn.title = title;
    btn.textContent = title.length > 14 ? title.slice(0, 14) + '…' : title;
    btn.addEventListener('click', () => {
      const entry = this._reg[id];
      if (!entry) return;
      if (entry.minimized) { this.restore(id); }
      else                  { this._front(entry.el); }
    });
    bar.appendChild(btn);
    this._taskBtns[id] = btn;
  }

  _removeTaskBtn(id) {
    this._taskBtns[id]?.remove();
    delete this._taskBtns[id];
  }

  close(id) {
    const e = this._reg[id];
    if (!e) return;
    e.el.remove();
    this._removeTaskBtn(id);
    delete this._reg[id];
  }

  minimize(id) {
    const e = this._reg[id];
    if (!e) return;
    e.el.style.display = 'none';
    e.minimized = true;
    this._taskBtns[id]?.classList.remove('mg-btn-active');
  }

  restore(id) {
    const e = this._reg[id];
    if (!e) return;
    e.el.style.display = 'flex';
    e.minimized = false;
    // If this window was never in the taskbar (was hidden on load), add it now
    if (!this._taskBtns[id]) {
      const title = e.el.querySelector('.mg-window-title')?.textContent || id;
      this._addTaskBtn(id, title);
    }
    this._front(e.el);
  }

  // Create a brand-new window dynamically and add it to the desktop.
  create(opts) {
    const {
      id,
      title    = 'Window',
      content  = '',
      x        = 120,
      y        = 100,
      width    = 300,
      className = '',
      icon     = '🖥'
    } = opts;

    if (document.getElementById(id)) {
      // Already exists — just bring to front
      this.restore(id);
      return document.getElementById(id);
    }

    const win = document.createElement('div');
    win.id        = id;
    win.className = ('mg-window ' + className).trim();
    win.style.cssText = `left:${x}px; top:${y}px; width:${width}px;`;

    win.innerHTML = `
      <div class="mg-window-titlebar">
        <span class="mg-window-icon">${icon}</span>
        <span class="mg-window-title">${title}</span>
        <div class="mg-window-controls">
          <button class="mg-btn-minimize" title="Minimize">&#x2012;</button>
        </div>
      </div>
      <div class="mg-window-content">${content}</div>
    `;

    document.getElementById('mg-windows-container').appendChild(win);

    requestAnimationFrame(() => win.classList.add('mg-window-enter'));

    this._register(win);
    this._front(win);
    return win;
  }
}
